import * as React from "react";
import {useCallback, useMemo} from "react";
import {Link} from "react-router-dom";
import {getTranslation} from "../../utils/translationUtil";
import {HighlightText} from "./HighlightText.component";
import {DisplayProductSummary} from "../../model/ResearchPageState.model";

export interface IResearchTableColumn {
    key: string;
    label: string;
    defaultLabel: string;
    colClass: string;
}

export const RESEARCH_TABLE_COLUMNS: IResearchTableColumn[] = [
    {key: "managerName", label: "research-page.manager-name", defaultLabel: "Manager Name", colClass: "research-table__col-manager"},
    {key: "name", label: "research-page.strategy-name", defaultLabel: "Strategy Name", colClass: "research-table__col-strategy"},
    {key: "assetType", label: "research-page.asset-class", defaultLabel: "Asset Class", colClass: "research-table__col-asset-class"},
    {key: "productRating", label: "research-page.rating", defaultLabel: "Rating", colClass: "research-table__col-rating"},
    {key: "oddRatingCurrent", label: "research-page.odd-rating", defaultLabel: "ODD Rating", colClass: "research-table__col-odd-rating"},
    {key: "approach", label: "research-page.approach", defaultLabel: "Approach", colClass: "research-table__col-approach"},
    {key: "category", label: "research-page.category", defaultLabel: "Category", colClass: "research-table__col-category"},
    {key: "region", label: "research-page.region", defaultLabel: "Region", colClass: "research-table__col-region"},
    {key: "style", label: "research-page.style", defaultLabel: "Style", colClass: "research-table__col-style"},
];

const REMOVE_COLUMN: IResearchTableColumn = {
    key: "remove",
    label: "research-page.remove",
    defaultLabel: "Remove",
    colClass: "research-table__col-remove",
};

export interface IResearchTableProps {
    columns: IResearchTableColumn[];
    data: DisplayProductSummary[];
    searchText: string;
    totalFiltered: number;
    caption?: string;
    onNavigateToProduct?: (backstopId: number) => void;
    onNavigateToManager?: (managerBackstopId: number) => void;
    editMode?: boolean;
    onRemoveProduct?: (backstopId: number) => void;
}

export const ResearchTable: React.FunctionComponent<IResearchTableProps> = (props) => {
    const {
        columns, data, searchText, totalFiltered, caption,
        onNavigateToProduct, onNavigateToManager,
        editMode, onRemoveProduct,
    } = props;

    const allColumns = useMemo(() => {
        return editMode ? [...columns, REMOVE_COLUMN] : columns;
    }, [columns, editMode]);

    const highlight = useCallback(
        (text: string) => <HighlightText text={text} search={searchText} />,
        [searchText]
    );

    const renderCell = (product: DisplayProductSummary, col: IResearchTableColumn) => {
        if (col.key === "name" && onNavigateToProduct) {
            return (
                <Link to={`/products/${product.backstopId}`}
                      onClick={() => onNavigateToProduct(product.backstopId)}>
                    <span id={`product_${product.backstopId}`}>
                        {highlight(product.name)}
                    </span>
                </Link>
            );
        }
        if (col.key === "managerName" && onNavigateToManager) {
            return (
                <Link to={`/managers/${product.manager.managerBackstopId}`}
                      onClick={() => onNavigateToManager(product.manager.managerBackstopId)}>
                    {highlight(product.managerName || product.manager.name)}
                </Link>
            );
        }
        if (col.key === "remove" && onRemoveProduct) {
            return (
                <button
                    type="button"
                    className="research-page__remove-link"
                    onClick={() => onRemoveProduct(product.backstopId)}
                    aria-label={`Remove ${product.name}`}
                >
                    <i className="fa-solid fa-xmark" aria-hidden="true" />
                </button>
            );
        }
        const value = String(product[col.key as keyof DisplayProductSummary] ?? "");
        return highlight(value);
    };

    const renderRow = (product: DisplayProductSummary) => {
        return (
            <tr key={product.backstopId}>
                {allColumns.map((col) => (
                    <td key={col.key}>{renderCell(product, col)}</td>
                ))}
            </tr>
        );
    };

    const tableCaption = caption ?? getTranslation("research-page.table-caption", "Research strategies table");

    return (
        <div className="research-page__table-container">
            <table className="research-table" data-testid="research-table">
                <caption className="research-table__caption">{tableCaption}</caption>
                <colgroup>
                    {allColumns.map((col) => <col key={col.key} className={col.colClass} />)}
                </colgroup>
                <thead>
                    <tr>
                        {allColumns.map((col) => (
                            <th key={col.key} scope="col">{getTranslation(col.label, col.defaultLabel)}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {data.map(renderRow)}
                </tbody>
            </table>

            {totalFiltered === 0 && (
                <div className="research-page__no-results" role="status">
                    {getTranslation("research-page.no-results", "No strategies match your current filters.")}
                </div>
            )}
        </div>
    );
};
